import type { AttachmentItem } from "hooks/useEmailsRestApi";
import { restClient } from "lib/rest-client";
import { type FC, useCallback, useState } from "react";
import { toastError } from "utils/toast-utils";

type AttachmentListProps = {
  attachments: AttachmentItem[];
  emailId: string;
};

/**
 * ファイルサイズを人間が読みやすい形式にフォーマット
 * @param {number} bytes - バイト数
 * @return {string} フォーマットされたサイズ文字列
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Content-Type からファイルアイコンのラベルを取得
 * @param {string} contentType - MIMEタイプ
 * @return {string} アイコン用ラベル
 */
const getFileIcon = (contentType: string): string => {
  if (contentType.startsWith("image/")) return "IMG";
  if (contentType === "application/pdf") return "PDF";
  if (
    contentType.includes("spreadsheet") ||
    contentType.includes("excel") ||
    contentType === "text/csv"
  )
    return "XLS";
  if (contentType.includes("word") || contentType.includes("document"))
    return "DOC";
  if (
    contentType.includes("presentation") ||
    contentType.includes("powerpoint")
  )
    return "PPT";
  if (contentType.startsWith("text/")) return "TXT";
  return "FILE";
};

/**
 * 添付ファイル一覧コンポーネント
 * @param {AttachmentListProps} props - 添付ファイル情報とメールID
 * @return {JSX.Element | null} 添付ファイル一覧UI
 */
export const AttachmentList: FC<AttachmentListProps> = ({
  attachments,
  emailId,
}) => {
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);

  // インライン画像を除外
  const fileAttachments = attachments.filter(a => !a.isInline);

  const handleDownload = useCallback(
    async (attachment: AttachmentItem) => {
      if (downloadingKey) return;
      setDownloadingKey(attachment.s3Key);
      try {
        const data = await restClient.get<{ url: string }>(
          `/emails/${emailId}/attachments/${attachment.s3Key}/download`,
        );
        window.open(data.url, "_blank");
      } catch (_e) {
        toastError("ダウンロードURLの取得に失敗しました。");
      } finally {
        setDownloadingKey(null);
      }
    },
    [emailId, downloadingKey],
  );

  if (fileAttachments.length === 0) return null;

  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">
        添付ファイル ({fileAttachments.length})
      </h4>
      <div className="flex flex-wrap gap-2">
        {fileAttachments.map(attachment => (
          <button
            key={attachment.s3Key}
            type="button"
            onClick={() => handleDownload(attachment)}
            disabled={downloadingKey === attachment.s3Key}
            className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors text-left text-sm disabled:opacity-50 disabled:cursor-wait"
            title={attachment.filename}
          >
            <span className="inline-flex items-center justify-center w-8 h-8 rounded bg-blue-100 text-blue-700 text-xs font-bold shrink-0">
              {getFileIcon(attachment.contentType)}
            </span>
            <div className="min-w-0">
              <div className="truncate max-w-[200px] text-gray-800">
                {attachment.filename}
              </div>
              <div className="text-xs text-gray-500">
                {formatFileSize(attachment.size)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
